@file:Suppress("DEPRECATION")
package org.example.newportfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.style.ComponentStyle
import com.varabyte.kobweb.silk.components.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.components.style.hover
import com.varabyte.kobweb.silk.components.style.toModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.newportfolio.components.widgets.animations.ScrollAnimator
import org.example.newportfolio.components.widgets.button.PrimaryButton
import org.example.newportfolio.models.Work
import org.example.newportfolio.theme.brand
import org.example.newportfolio.theme.fonts.*
import org.jetbrains.compose.web.css.*

// The case-study pages live in the same always-dark, immersive world as the
// product pages (deliberately NOT color-mode aware — the photography and
// scrims are tuned for one canvas). Single source of truth for that canvas:
private val PageBg = Color.rgb(14, 13, 22)

// ──── Cinematic hero banner ──────────────────────────────────────────────────
val ProjectHeroStyle by ComponentStyle {
    base {
        Modifier
            .fillMaxWidth()
            .height(280.px)
            .position(Position.Relative)
            .overflow(Overflow.Hidden)
    }
    Breakpoint.MD { Modifier.height(320.px) }
    Breakpoint.LG { Modifier.height(380.px) }
    Breakpoint.XL { Modifier.height(420.px) }
}

// ──── Category chip ──────────────────────────────────────────────────────────
val ProjectCategoryChipStyle by ComponentStyle {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .padding(leftRight = 16.px, topBottom = 7.px)
            .borderRadius(20.px)
            .backgroundColor(palette.brand.primary.toRgb().copyf(alpha = 0.16f))
            .border(1.px, LineStyle.Solid, palette.brand.primary.toRgb().copyf(alpha = 0.45f))
            .color(Colors.White)
            .fontSize(13.px)
            .letterSpacing(0.5.px)
            .styleModifier { property("backdrop-filter", "blur(10px)") }
    }
}

// ──── Key-highlight card ─────────────────────────────────────────────────────
val HighlightCardStyle by ComponentStyle {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .padding(22.px)
            .borderRadius(18.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.04f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.08f))
            .transition(
                CSSTransition("transform", 0.25.s, TransitionTimingFunction.EaseOut),
                CSSTransition("border-color", 0.25.s, TransitionTimingFunction.EaseOut),
                CSSTransition("box-shadow", 0.25.s, TransitionTimingFunction.EaseOut)
            )
    }
    hover {
        Modifier
            .transform { translateY((-4).px) }
            .border(1.px, LineStyle.Solid, palette.brand.primary.toRgb().copyf(alpha = 0.45f))
            .boxShadow(0.px, 12.px, 30.px, 0.px, palette.brand.primary.toRgb().copyf(alpha = 0.15f))
    }
}

// ──── Sticky side card (tech + links) ────────────────────────────────────────
val ProjectSideCardStyle by ComponentStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(28.px)
            .borderRadius(24.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.03f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.08f))
    }
}

// ──── Secondary store/demo link button ───────────────────────────────────────
val ProjectSecondaryLinkStyle by ComponentStyle {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .fillMaxWidth()
            .padding(leftRight = 18.px, topBottom = 11.px)
            .borderRadius(12.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.05f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.14f))
            .color(Color.rgba(255, 255, 255, 0.88f))
            .fontSize(14.px)
            .cursor(Cursor.Pointer)
            .textAlign(TextAlign.Center)
            .transition(
                CSSTransition("border-color", 0.2.s, TransitionTimingFunction.EaseOut),
                CSSTransition("background-color", 0.2.s, TransitionTimingFunction.EaseOut)
            )
    }
    hover {
        Modifier
            .backgroundColor(Color.rgba(159, 52, 240, 0.12f))
            .border(1.px, LineStyle.Solid, palette.brand.primary.toRgb().copyf(alpha = 0.5f))
    }
}

// ──── Prev / next project navigation card ────────────────────────────────────
val ProjectNavCardStyle by ComponentStyle {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .fillMaxWidth()
            .padding(24.px)
            .borderRadius(20.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.03f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.08f))
            .cursor(Cursor.Pointer)
            .transition(
                CSSTransition("transform", 0.25.s, TransitionTimingFunction.EaseOut),
                CSSTransition("border-color", 0.25.s, TransitionTimingFunction.EaseOut),
                CSSTransition("background-color", 0.25.s, TransitionTimingFunction.EaseOut)
            )
    }
    hover {
        Modifier
            .transform { translateY((-4).px) }
            .backgroundColor(Color.rgba(159, 52, 240, 0.07f))
            .border(1.px, LineStyle.Solid, palette.brand.primary.toRgb().copyf(alpha = 0.5f))
    }
}

@Page("/project/{name}")
@Composable
fun ProjectPage() {
    val ctx = rememberPageContext()
    val bp = rememberBreakpoint()
    val palette = ColorMode.current.toPalette()

    // The prev/next cards navigate between projects on this SAME dynamic
    // route, and in this Kobweb version a same-page navigateTo() updates the
    // URL without recomposing with the new param. So prev/next clicks set an
    // override slug (plus pushState for the URL bar), while the live route
    // param — read fresh every composition — covers normal page entries. A
    // popstate listener keeps browser back/forward working between projects.
    val routeSlug = ctx.route.params["name"] ?: ""
    var overrideSlug by remember { mutableStateOf<String?>(null) }
    DisposableEffect(Unit) {
        val onPopState: (org.w3c.dom.events.Event) -> Unit = {
            val path = window.location.pathname
            if (path.startsWith("/project/")) {
                overrideSlug = path.removePrefix("/project/").trim('/')
            }
        }
        window.addEventListener("popstate", onPopState)
        onDispose { window.removeEventListener("popstate", onPopState) }
    }
    val slug = overrideSlug ?: routeSlug

    val work = Work.entries.find { it.name.lowercase() == slug.lowercase() }

    if (work == null) {
        // Unknown slug → back to the portfolio rather than a dead end.
        // REPLACE, not the default PUSH: pushing would leave /project/bogus
        // in history, and pressing Back would remount this page, re-fire the
        // redirect, and trap the user in a back-button loop.
        if (slug.isNotEmpty()) {
            LaunchedEffect(slug) { ctx.router.navigateTo("/", UpdateHistoryMode.REPLACE) }
        }
        return
    }

    // SPA navigation keeps the previous scroll offset; a case study must
    // always open at its hero. Also give the tab a real title.
    LaunchedEffect(work) {
        window.scrollTo(0.0, 0.0)
        document.title = "${work.workName} • Mohamed Elgohary"
    }

    val all = Work.entries
    val index = all.indexOf(work)
    val prev = all[(index - 1 + all.size) % all.size]
    val next = all[(index + 1) % all.size]

    // Flagship full-stack builds ship a dedicated Product Showcase (phone
    // frames + browser-chrome dashboard cards) instead of the single inline
    // figure used by the smaller projects.
    val hasShowcase = work.mobileShots.isNotEmpty() || work.dashboardShots.isNotEmpty()

    val sidePadding = when {
        bp >= Breakpoint.MD -> 60.px
        else -> 20.px
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            .backgroundColor(PageBg)
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            // ─── Atmospheric hero ────────────────────────────────────────
            // Typography-led: the project's own screenshot, blurred beyond
            // recognition and pulled into the brand's purple world, becomes
            // an abstract color field unique to each project. The sharp
            // screenshot is shown as a framed figure in the article body
            // instead — work images range from landscape banners to
            // full-page portrait captures, and none of them survive a
            // full-bleed hero crop gracefully.
            Box(modifier = ProjectHeroStyle.toModifier()) {
                Image(
                    src = "/${work.imageRes}",
                    alt = "",
                    modifier = Modifier
                        .fillMaxSize()
                        .objectFit(ObjectFit.Cover)
                        .styleModifier {
                            property("filter", "blur(56px) brightness(0.4) saturate(1.4)")
                            // Over-scale so the blur never reveals hard edges.
                            property("transform", "scale(1.2)")
                        }
                )
                // Brand tint pulls every project's ambience — grey, white,
                // whatever the screenshot happens to be — into the site's
                // purple world.
                Box(
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property(
                            "background",
                            "linear-gradient(135deg, rgba(89,29,135,0.50) 0%, rgba(14,13,22,0.30) 55%, rgba(159,52,240,0.22) 100%)"
                        )
                    }
                )
                // Scrim: legible back button up top, seamless dissolve into
                // the page canvas at the bottom (the title overlaps this edge).
                Box(
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property(
                            "background",
                            "linear-gradient(to bottom, rgba(14,13,22,0.45) 0%, rgba(14,13,22,0.0) 30%, " +
                                "rgba(14,13,22,0.35) 62%, rgba(14,13,22,0.9) 86%, rgb(14,13,22) 100%)"
                        )
                        property("pointer-events", "none")
                    }
                )
                Box(
                    modifier = BackButtonStyle.toModifier()
                        .position(Position.Absolute)
                        .top(20.px)
                        .left(sidePadding)
                        .zIndex(5)
                        .styleModifier { property("backdrop-filter", "blur(10px)") }
                        .onClick { ctx.router.navigateTo("/#works") }
                ) {
                    SpanText("← Back to Works")
                }
            }

            // ─── Content column (overlaps the hero's faded bottom edge) ──
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(1120.px)
                    .margin(leftRight = autoLength)
                    // Pull the title block up over the hero's faded bottom
                    // edge. Longhand so it doesn't clobber the auto side
                    // margins that center this column.
                    .styleModifier { property("margin-top", "-96px") }
                    .padding(left = sidePadding, right = sidePadding, bottom = 80.px)
                    .position(Position.Relative)
                    .zIndex(2)
            ) {
                // Title block
                Column(modifier = Modifier.fillMaxWidth().classNames("scroll-reveal", "stagger-0")) {
                    Box(modifier = ProjectCategoryChipStyle.toModifier().margin(bottom = 18.px)) {
                        SpanText(work.category)
                    }
                    SpanText(
                        text = work.workName,
                        modifier = TextStyle.toModifier(DisplayTextStyle)
                            .fontSize(if (bp >= Breakpoint.MD) 52.px else 30.px)
                            .lineHeight(1.15)
                            .color(Colors.White)
                            .margin(bottom = 18.px)
                    )
                    Box(
                        modifier = Modifier
                            .width(80.px)
                            .height(3.px)
                            .borderRadius(2.px)
                            .backgroundColor(palette.brand.primary)
                            .margin(bottom = 36.px)
                    )
                }

                // Editorial two-column: prose on the left, tech/links card
                // on the right. Stacks on phones.
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .gap(if (bp >= Breakpoint.LG) 48.px else 32.px)
                        .flexWrap(if (bp >= Breakpoint.LG) FlexWrap.Nowrap else FlexWrap.Wrap),
                    verticalAlignment = Alignment.Top
                ) {
                    Column(
                        modifier = Modifier
                            .minWidth(0.px)
                            .styleModifier { property("flex", "1 1 560px") }
                            .classNames("scroll-reveal", "stagger-1")
                    ) {
                        SectionLabel("ABOUT THE PROJECT")
                        SpanText(
                            text = work.description,
                            modifier = TextStyle.toModifier(BodyLargeTextStyle)
                                .lineHeight(1.85)
                                .color(Color.rgba(255, 255, 255, 0.78f))
                                // Descriptions may embed bare URLs; without
                                // this an unbreakable token wider than a phone
                                // viewport drags the whole page into
                                // horizontal scroll.
                                .styleModifier { property("overflow-wrap", "anywhere") }
                        )
                        // Smaller projects show a single framed figure inline
                        // with the prose. Flagship full-stack builds skip it —
                        // their screenshots live in the full-width Product
                        // Showcase below, where phone and dashboard captures
                        // each get a presentation tuned to their shape.
                        if (!hasShowcase) {
                            Box(modifier = Modifier.fillMaxWidth().margin(top = 32.px)) {
                                ProjectFigure(
                                    src = "/${work.imageRes}",
                                    alt = "${work.workName} project screenshot"
                                )
                            }
                        }
                    }

                    Column(
                        modifier = Modifier
                            .styleModifier { property("flex", "1 1 320px") }
                            .classNames("scroll-reveal", "stagger-2")
                    ) {
                        Column(modifier = ProjectSideCardStyle.toModifier()) {
                            SectionLabel("TECH STACK")
                            Row(
                                modifier = Modifier.fillMaxWidth().gap(10.px).flexWrap(FlexWrap.Wrap)
                            ) {
                                work.techStack.forEach { tech ->
                                    Box(modifier = ProductTechBadgeStyle.toModifier()) {
                                        SpanText(tech)
                                    }
                                }
                            }
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(1.px)
                                    .margin(topBottom = 24.px)
                                    .backgroundColor(Color.rgba(255, 255, 255, 0.08f))
                            )
                            SectionLabel("LINKS")
                            PrimaryButton(
                                text = work.linkLabel,
                                onClick = { window.open(work.link, "_blank") }
                            )
                            if (work.extraLinks.isNotEmpty()) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .margin(top = 14.px)
                                        .gap(10.px)
                                ) {
                                    work.extraLinks.forEach { extra ->
                                        Box(
                                            modifier = ProjectSecondaryLinkStyle.toModifier()
                                                .onClick { window.open(extra.url, "_blank") },
                                            contentAlignment = Alignment.Center
                                        ) {
                                            SpanText(extra.label)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // ─── Product showcase (full width) ───────────────────────
                // Mobile captures sit in portrait phone frames; dashboard
                // captures sit in wide browser-chrome cards. Going full-width
                // here (outside the editorial two-column row) lets landscape
                // dashboards breathe.
                if (hasShowcase) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(top = 64.px)
                            .classNames("scroll-reveal", "stagger-1")
                    ) {
                        SectionLabel("PRODUCT SHOWCASE")

                        if (work.mobileShots.isNotEmpty()) {
                            ShowcaseGroupLabel("MOBILE APP", topMargin = 4)
                            SimpleGrid(
                                numColumns = numColumns(base = 2, md = 3),
                                modifier = Modifier.fillMaxWidth().gap(16.px)
                            ) {
                                work.mobileShots.forEach { shot ->
                                    PhoneFrame(
                                        src = "/$shot",
                                        alt = "${work.workName} mobile app screen"
                                    )
                                }
                            }
                        }

                        if (work.dashboardShots.isNotEmpty()) {
                            ShowcaseGroupLabel(
                                "WEB DASHBOARD",
                                topMargin = if (work.mobileShots.isNotEmpty()) 44 else 4
                            )
                            Column(modifier = Modifier.fillMaxWidth().gap(20.px)) {
                                work.dashboardShots.forEach { shot ->
                                    BrowserFrame(
                                        src = "/$shot",
                                        alt = "${work.workName} web dashboard screen"
                                    )
                                }
                            }
                        }
                    }
                }

                // ─── Key highlights ──────────────────────────────────────
                if (work.highlights.isNotEmpty()) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(top = 64.px)
                            .classNames("scroll-reveal", "stagger-1")
                    ) {
                        SectionLabel("KEY HIGHLIGHTS")
                        SimpleGrid(
                            numColumns = numColumns(base = 1, md = 2),
                            modifier = Modifier.fillMaxWidth().gap(16.px)
                        ) {
                            work.highlights.forEachIndexed { i, highlight ->
                                Row(
                                    modifier = HighlightCardStyle.toModifier().gap(16.px),
                                    verticalAlignment = Alignment.Top
                                ) {
                                    SpanText(
                                        text = (i + 1).toString().padStart(2, '0'),
                                        modifier = Modifier
                                            .color(palette.brand.primary)
                                            .fontSize(15.px)
                                            .fontWeight(FontWeight.Bold)
                                            .letterSpacing(1.px)
                                    )
                                    SpanText(
                                        text = highlight,
                                        modifier = TextStyle.toModifier(BodyLargeTextStyle)
                                            .fontSize(15.px)
                                            .lineHeight(1.7)
                                            .color(Color.rgba(255, 255, 255, 0.82f))
                                    )
                                }
                            }
                        }
                    }
                }

                // ─── Prev / next case study ──────────────────────────────
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(top = 72.px)
                        .classNames("scroll-reveal", "stagger-2")
                ) {
                    SectionLabel("MORE CASE STUDIES")
                    SimpleGrid(
                        numColumns = numColumns(base = 1, md = 2),
                        modifier = Modifier.fillMaxWidth().gap(16.px)
                    ) {
                        ProjectNavCard(
                            direction = "← PREVIOUS",
                            work = prev,
                            alignEnd = false,
                            onClick = {
                                overrideSlug = prev.name.lowercase()
                                window.history.pushState(null, "", "/project/${prev.name.lowercase()}")
                            }
                        )
                        ProjectNavCard(
                            direction = "NEXT →",
                            work = next,
                            alignEnd = bp >= Breakpoint.MD,
                            onClick = {
                                overrideSlug = next.name.lowercase()
                                window.history.pushState(null, "", "/project/${next.name.lowercase()}")
                            }
                        )
                    }
                }
            }
        }

        // Same animation driver as the home page: powers the scroll-reveal
        // classes above (one-shot IntersectionObserver), the cursor blob and
        // the scroll progress bar.
        ScrollAnimator()
    }
}

@Composable
private fun ProjectFigure(src: String, alt: String) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(16.px)
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.10f))
            .overflow(Overflow.Hidden)
            .boxShadow(0.px, 18.px, 50.px, 0.px, Color.rgba(0, 0, 0, 0.45f))
    ) {
        Image(
            src = src,
            alt = alt,
            modifier = Modifier
                .fillMaxWidth()
                .maxHeight(560.px)
                .objectFit(ObjectFit.Cover)
                .styleModifier {
                    property("object-position", "50% 0%")
                    property("display", "block")
                }
        )
    }
}

// Brand purple used for the small showcase accents (kept as a literal so the
// helper composables don't need the page's palette plumbed through).
private val BrandAccent = Color.rgb(159, 52, 240)

@Composable
private fun ShowcaseGroupLabel(text: String, topMargin: Int) {
    Row(
        modifier = Modifier.margin(top = topMargin.px, bottom = 18.px).gap(10.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .width(22.px)
                .height(2.px)
                .borderRadius(2.px)
                .backgroundColor(BrandAccent)
        )
        SpanText(
            text = text,
            modifier = Modifier
                .color(Color.rgba(255, 255, 255, 0.62f))
                .fontSize(12.px)
                .letterSpacing(1.5.px)
        )
    }
}

// Portrait phone frame: a subtle bezel with the screen contained inside. The
// aspect-ratio is locked to the source capture (1206 × 2622) so the screen
// fills it with no perceptible crop.
@Composable
private fun PhoneFrame(src: String, alt: String) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("aspect-ratio", "1206 / 2622") }
            .borderRadius(26.px)
            .padding(6.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.05f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.12f))
            .boxShadow(0.px, 16.px, 40.px, 0.px, Color.rgba(0, 0, 0, 0.45f))
            .overflow(Overflow.Hidden)
    ) {
        Image(
            src = src,
            alt = alt,
            modifier = Modifier
                .fillMaxSize()
                .borderRadius(21.px)
                .objectFit(ObjectFit.Cover)
                .styleModifier { property("display", "block") }
        )
    }
}

// Wide dashboard card with a faux browser chrome bar. The image keeps its
// natural height so the full landscape capture is visible (no crop).
@Composable
private fun BrowserFrame(src: String, alt: String) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(14.px)
            .overflow(Overflow.Hidden)
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.10f))
            .boxShadow(0.px, 18.px, 50.px, 0.px, Color.rgba(0, 0, 0, 0.45f))
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(34.px)
                .backgroundColor(Color.rgba(255, 255, 255, 0.06f))
                .padding(leftRight = 14.px)
                .gap(7.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            repeat(3) {
                Box(
                    modifier = Modifier
                        .width(10.px)
                        .height(10.px)
                        .borderRadius(50.percent)
                        .backgroundColor(Color.rgba(255, 255, 255, 0.18f))
                )
            }
        }
        Image(
            src = src,
            alt = alt,
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier { property("display", "block") }
        )
    }
}

@Composable
private fun SectionLabel(text: String) {
    SpanText(
        text = text,
        modifier = TextStyle.toModifier(LabelLargeTextStyle)
            .color(Color.rgba(255, 255, 255, 0.45f))
            .fontSize(13.px)
            .letterSpacing(2.px)
            .margin(bottom = 16.px)
    )
}

@Composable
private fun ProjectNavCard(
    direction: String,
    work: Work,
    alignEnd: Boolean,
    onClick: () -> Unit,
) {
    Column(
        modifier = ProjectNavCardStyle.toModifier().onClick { onClick() },
        horizontalAlignment = if (alignEnd) Alignment.End else Alignment.Start
    ) {
        SpanText(
            text = direction,
            modifier = Modifier
                .color(Color.rgba(255, 255, 255, 0.45f))
                .fontSize(12.px)
                .letterSpacing(2.px)
                .margin(bottom = 10.px)
        )
        SpanText(
            text = work.workName,
            modifier = TextStyle.toModifier(TitleTextStyle)
                .color(Colors.White)
                .textAlign(if (alignEnd) TextAlign.End else TextAlign.Start)
        )
        SpanText(
            text = work.category,
            modifier = Modifier
                .color(Color.rgba(255, 255, 255, 0.55f))
                .fontSize(13.px)
                .margin(top = 6.px)
        )
    }
}
